import { Typography } from '@mui/material';
import { InfoBox } from '@resurs-internal/mui';
import { ChangeEvent, useEffect, useState } from 'react';

import { Debt, getDebtsService } from '../../api/getDebts';
import { initNorskGjeldService } from '../../api/initNorskGjeld';
import { callbackUrl } from '../../api/util';
import Description from '../../components/Description/Description';
import { ErrorMessage } from '../../components/ErrorMessage';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import ShowDetailsModal from '../../components/ShowDetailsModal/ShowDetailsModal';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { ConsumerLoanTargetPath } from '../../domain/types';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useArnFromPath from '../../hooks/useArnFromPath';
import { useConsentIdParams } from '../../hooks/useConsentIdParams';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useSettings from '../../hooks/useSettings';
import useTranslation from '../../hooks/useTranslation';
import { addOrUpdateFormField } from '../../store/applicationform';
import { selectFieldStateByName } from '../../store/applicationform/applicationFormSelectors';
import { setConsolidations, setInternalLoans } from '../../store/applicationform/applicationFormStore';
import { WizardPageAction } from '../../store/wizard/wizard';
import ConsolidationCheckboxes from './ConsolidationCheckboxes';

const PARAM_CONSENT_STATE = 'consent_state';
const CONSENT_STATE_ERROR = 'error';
const CONSENT_STATE_SUCCESS = 'success';
const CO_APPLICANT_GOVERNMENT_ID = 'coapplicantGovernmentId';
const CO_APPLICANT_NORSK_GJELD_REFERENCE_ID = 'coapplicantGjeldsregisteretId';

type CoApplicantConsolidationPageProps = {
    isConsolidation?: boolean;
};

const CoApplicantConsolidationPage = ({ isConsolidation }: CoApplicantConsolidationPageProps) => {
    useSetBannerType('bannerOne');
    const { countryCode, languageCode } = useSettings();
    const translate = useTranslation();
    const dispatch = useAppDispatch();
    const { currentPage } = usePagesContext();
    const { coApplicantTotalSections, coApplicantTotalAboutYouPages } = usePagesConfig();
    const defaultPagesAction = useDefaultPagesAction();
    const { apiKey, proxyBaseUrl } = useSettings();
    const [errorOccurred, setErrorOccurred] = useState<boolean>(false);
    const [debts, setDebts] = useState<Debt[]>([]);
    useConsentIdParams('coapplicantGovernmentId');

    const { authenticationData } = useAppSelector((state) => state.authentication);
    const selectedConsolidations = useAppSelector((state) => state.form.consolidations);
    const coapplicantGovernmentId = useAppSelector(selectFieldStateByName)(CO_APPLICANT_GOVERNMENT_ID)?.value;
    const coapplicantNorskGjeldReferenceIdFieldState = useAppSelector(selectFieldStateByName)(
        CO_APPLICANT_NORSK_GJELD_REFERENCE_ID,
    );
    const applicationReferenceNumber = useArnFromPath();

    useEffect(() => {
        if (!coapplicantNorskGjeldReferenceIdFieldState?.value) {
            return;
        }
        if (isConsolidation === undefined) {
            // Summary not loaded yet — wait before deciding so we don't skip debts
            // on a real consolidation just because the summary hasn't arrived.
            return;
        }
        if (!isConsolidation) {
            // CASH: consent collected, debts are not shown — go straight to the next page.
            defaultPagesAction('next');
            return;
        }
        getDebtsService(
            proxyBaseUrl,
            apiKey,
            coapplicantNorskGjeldReferenceIdFieldState.value + '',
            'CO',
            authenticationData,
        )
            .then((response) => {
                setDebts(response.debts);
                const internalLoans = response.debts.filter((debt) => debt.nonSelectable);
                dispatch(setInternalLoans(internalLoans));
                const newDebts = selectedConsolidations.filter((consolidation) =>
                    response.debts.find((debt) => debt.internalId === consolidation.internalId),
                );

                dispatch(setConsolidations(newDebts));
            })
            .catch(() => {
                setDebts([]);
                setErrorOccurred(true);
                dispatch(setConsolidations([]));
                dispatch(setInternalLoans([]));
                dispatch(
                    addOrUpdateFormField({
                        fieldName: CO_APPLICANT_NORSK_GJELD_REFERENCE_ID,
                        value: undefined,
                        hasError: true,
                    }),
                );
            });
    }, [coapplicantNorskGjeldReferenceIdFieldState, isConsolidation]);

    const onAction = async (action: WizardPageAction) => {
        if (action === 'collect-norskgjeld') {
            try {
                const successUrl = `${callbackUrl(
                    ConsumerLoanTargetPath.coapplicant,
                    languageCode,
                    countryCode,
                    applicationReferenceNumber ?? '',
                )}?${PARAM_CONSENT_STATE}=${CONSENT_STATE_SUCCESS}`;
                const failureUrl = `${callbackUrl(
                    ConsumerLoanTargetPath.coapplicant,
                    languageCode,
                    countryCode,
                    applicationReferenceNumber ?? '',
                )}?${PARAM_CONSENT_STATE}=${CONSENT_STATE_ERROR}`;

                const response = await initNorskGjeldService(
                    proxyBaseUrl,
                    apiKey,
                    coapplicantGovernmentId as string,
                    successUrl,
                    failureUrl,
                    authenticationData,
                );
                window.location.replace(response.consentUrl);
            } catch (error) {
                setErrorOccurred(true);
                throw error;
            }
            return false;
        } else if (action === 'consolidation-next') {
            return defaultPagesAction(action);
        } else if (action === 'next') {
            return defaultPagesAction(action);
        }
        return defaultPagesAction(action);
    };

    const handleSelectConsolidation = (event: ChangeEvent<HTMLInputElement>, internalId: string) => {
        const isChecked = event.target.checked;
        const newDebts = debts.filter((debt) => {
            // Never add non-selectable debts to consolidations
            if (debt.nonSelectable) {
                return false;
            }

            const isMatching = debt.internalId === internalId;
            const isAlreadySelected = !!selectedConsolidations.find(
                (consolidation) => consolidation.internalId === debt.internalId,
            );

            if (isChecked) {
                return isMatching || isAlreadySelected;
            } else {
                return !isMatching && isAlreadySelected;
            }
        });

        dispatch(setConsolidations(newDebts));
    };

    return (
        <>
            <PagesHeader
                actions={['back']}
                onAction={onAction}
                currentPage={currentPage}
                currentSection={0}
                numberOfSections={coApplicantTotalSections}
                numberOfPagesInSection={coApplicantTotalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
            {!coapplicantNorskGjeldReferenceIdFieldState?.value ? (
                <>
                    <Title
                        text={translate(
                            // `undefined` (summary still loading) keeps the consolidation copy to
                            // avoid flashing CASH text before the real intent is known.
                            isConsolidation === false ? 'page.norsk-gjeld.cash.title' : 'page.norsk-gjeld.title',
                        )}
                    />
                    <Description
                        text={translate(
                            isConsolidation === false
                                ? 'page.norsk-gjeld.cash.description'
                                : 'page.norsk-gjeld.description',
                        )}
                    />
                </>
            ) : (
                <>
                    <Title text={translate('page.norsk-gjeld.consolidations.title')} />
                    {debts && debts.length !== 0 ? (
                        <ShowDetailsModal
                            label={translate('dialog.collect-norsk-gjeld.label')}
                            dialogTitle={translate('dialog.collect-norsk-gjeld.label')}
                            dialogContent={translate('dialog.collect-norsk-gjeld.content')}
                        />
                    ) : null}
                    {debts && debts.length > 0 && (
                        <>
                            <ConsolidationCheckboxes
                                debts={debts}
                                handleChange={handleSelectConsolidation}
                                selectedConsolidations={selectedConsolidations}
                            />
                        </>
                    )}

                    {debts && debts.length === 0 && (
                        <>
                            <Typography variant={'body1'} marginBottom={2} marginTop={2}>
                                {translate('page.co-applicant.consolidation-amount.description')}
                            </Typography>
                        </>
                    )}
                </>
            )}
            {errorOccurred && (
                <ErrorMessage
                    title={translate(`page.error.title`)}
                    description={translate(`page.authentication.submit.log.in.error.description`)}
                />
            )}
            {coapplicantNorskGjeldReferenceIdFieldState?.hasError && (
                <ErrorMessage
                    title={translate('page.norsk-gjeld.consolidations.error.title')}
                    description={translate('page.norsk-gjeld.consolidations.error.description')}
                />
            )}
            <InfoBox variant="no-title">{translate('field.co-applicant.additional-consolidation-info')}</InfoBox>
            <PagesFooter
                actions={
                    !coapplicantNorskGjeldReferenceIdFieldState?.value
                        ? ['collect-norskgjeld']
                        : (debts && debts.length === 0) || debts.some((debt) => debt.nonSelectable)
                          ? ['next']
                          : selectedConsolidations.length === 0
                            ? ['consolidation-next']
                            : ['next']
                }
                onAction={onAction}
                currentSection={0}
                numberOfSections={coApplicantTotalSections}
                numberOfPagesInSection={coApplicantTotalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
        </>
    );
};

export default CoApplicantConsolidationPage;
